import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { triggerNotification } from '../../../helpers/notificationHelper';
import CheckoutOrderSummary from './CheckoutOrderSummary';
import CheckoutItemList from './CheckoutItemList';
import OfflinePaymentModal from './OfflinePaymentModal';
import HeaderView from '../../common/HeaderView';
import AppTitle from '../../common/AppTitle';
import AppFlexbox from '../../common/AppFlexbox';
import AppCard from '../../common/AppCard';
import { ANALYTIC_MERCH_PACKAGE_SHOWCASE_LOCATION_ENUM } from '../../../config/constants';
import EcomProductCollectionShowcase from '../ecommerce/EcomProductCollectionShowcase';
import WebstoreProductsModal from '../ecommerce/WebstoreProductsModal';
import { useRegCartController } from '../../../helpers/shop/useRegCartController';
import WsUnavailableProductsAlert from '../ecommerce/webstore/sections/checkoutContent/WsUnavailableProductsAlert';
import WsCareInstructionsAlert from '../ecommerce/webstore/blocks/careInstructions/WsCareInstructionsAlert';
import ChangeShippingModal from './ChangeShippingModal';
import { useMediaQueries } from '../../../helpers/hooks';
import CustomizeCartProductModal from '../ecommerce/webstore/CustomizeCartProductModal';
import PreviewWebstoreProductModal from '../ecommerce/webstore/PreviewWebstoreProductModal';

const themeConfig = {};

const colorSchemes = [
  {
    textColor: '#000000',
    backgroundColor: '#ffffff'
  }
];

const CheckoutCart = ({ hasFetched }) => {
  const { isLaptopOrSmaller, isDesktopOrSmaller } = useMediaQueries();
  const {
    userCart,
    isCartFetching,
    getStorageCart,
    fetchUserCart,
    checkoutCart,
    removeCartProducts,
    updateCartProducts,
    redeemCartProductCoupon,
    removeUnavailableCartProducts,
    setFundraiser,
    showCartProductCustomization
  } = useRegCartController();
  const [checkoutState, setCheckoutState] = useState({
    pkRegCart: null,
    email: '',
    paymentType: '',
    showOfflinePaymentModal: false,
    fundraiser: {
      fkEcomStoreRosterPlayer: null,
      customInput: null
    },
    loading: false
  });
  const loading = isCartFetching || !userCart || !hasFetched;
  const regAssociation = userCart?.regAssociation;
  const merchEnabled =
    regAssociation?.merchEnabled && regAssociation?.ecomStore;

  const hasFinalSaleProducts = userCart?.ecomStoreCart?.ecomStoreCartProducts?.some(
    (p) =>
      p.ecomStoreProductVariant.ecomStoreProduct.ecomVendorProduct
        .ecomVendorProductExternalIds.length > 0
  );

  useEffect(() => {
    if (userCart && checkoutState.pkRegCart !== userCart.pkRegCart) {
      const storageCart = getStorageCart();
      setCheckoutState((c) => ({
        ...c,
        pkRegCart: userCart.pkRegCart,
        email: storageCart?.email || '',
        fundraiser: storageCart.fundraiser || {
          fkEcomStoreRosterPlayer: null,
          customInput: null
        },
        loading: false
      }));
    }
    else {
      setCheckoutState((c) => ({
        ...c,
        loading: false
      }));
    }
  }, [userCart]);

  const onError = (error) => {
    triggerNotification(error);
    setCheckoutState((c) => ({
      ...c,
      loading: false
    }));
  };

  const createCheckoutSession = (paymentType, paymentPlanId) => {
    const isCartCompleted =
      userCart.regCartProducts.length === 0 ||
      userCart.regCartProducts.every((p) => p.regFormSubmission.isCompleted);

    if (userCart.cart.lineItems.length === 0) {
      triggerNotification('Cart is empty');
    }
    else if (!isCartCompleted) {
      triggerNotification('Please complete all forms before checking out');
    }
    else if (paymentType === 'offline') {
      setCheckoutState((c) => ({
        ...c,
        paymentType,
        showOfflinePaymentModal: true
      }));
    }
    else if (
      userCart.subtotalInCents !== 0 &&
      userCart.subtotalInCents / 100 < 10
    ) {
      triggerNotification('Minimum order amount of $10 required');
    }
    else {
      setCheckoutState((c) => ({
        ...c,
        paymentType,
        loading: true
      }));
      checkoutCart(
        {
          paymentType,
          fkEcomStoreRosterPlayer:
            checkoutState.fundraiser.fkEcomStoreRosterPlayer,
          fundraiserCustomInput: checkoutState.fundraiser.customInput,
          paymentPlanId,
          shippingOptions: userCart.cart.shippingCosts.map((s) => ({
            value: s.value,
            label: s.label,
            cost: s.totalInCents,
            fkEcomExternalConnectionType: s.fkEcomExternalConnectionType,
            ecomExternalConnectionExternalId: s.ecomExternalConnectionExternalId
          }))
        },
        ({ sessionUrl, outOfSync, outOfStock }) => {
          if (outOfSync) {
            triggerNotification(
              outOfStock
                ? 'Some items in your cart are out of stock. Please review your cart before proceeding to checkout.'
                : 'Products have changed since you added them to your cart. Please review your cart before proceeding to checkout.'
            );
            fetchUserCart({ includeShipping: true });
            setCheckoutState((c) => ({
              ...c,
              loading: false
            }));
          }
          else if (sessionUrl) {
            window.location.href = sessionUrl;
          }
          else {
            triggerNotification('Checkout Successful', 'Success', 'green');
            fetchUserCart();
          }
        },
        onError
      );
    }
  };

  const onUpdateCartProducts = (
    {
      regCartProducts,
      ecomStoreCartProducts,
      ecomStoreCartPackages,
      removeUnavailable
    },
    onSuccessCallback,
    onErrorCallback
  ) => {
    setCheckoutState({
      ...checkoutState,
      loading: true
    });
    updateCartProducts(
      {
        regCartProducts,
        ecomStoreCartProducts,
        ecomStoreCartPackages,
        removeUnavailable,
        includeShipping: true
      },
      () => {
        setCheckoutState({
          ...checkoutState,
          loading: false
        });
        if (onSuccessCallback) {
          onSuccessCallback();
        }
      },
      () => {
        setCheckoutState({
          ...checkoutState,
          loading: false
        });
        if (onErrorCallback) {
          onErrorCallback();
        }
      }
    );
  };

  const onRemoveCartProducts = ({
    pkRegCartProducts,
    pkEcomStoreCartProducts,
    pkEcomStoreCartPackages
  }) => {
    setCheckoutState({
      ...checkoutState,
      loading: true
    });
    removeCartProducts(
      {
        pkRegCartProducts,
        pkEcomStoreCartProducts,
        pkEcomStoreCartPackages,
        includeShipping: true
      },
      () => {
        setCheckoutState({
          ...checkoutState,
          loading: false
        });
      },
      onError
    );
  };

  const onRemoveUnavailableProducts = () => {
    setCheckoutState({
      ...checkoutState,
      loading: true
    });
    removeUnavailableCartProducts(
      { includeShipping: true },
      () => {
        setCheckoutState({
          ...checkoutState,
          loading: false
        });
        triggerNotification('Cart updated!', 'success', 'green');
      },
      onError
    );
  };

  const onRedeemProductCoupon = ({ pkRegCartProduct, code }) => {
    setCheckoutState({
      ...checkoutState,
      loading: true
    });
    redeemCartProductCoupon(
      { pkRegCartProduct, code },
      () => {
        triggerNotification(
          code ? 'Coupon redeemed' : 'Coupon removed',
          'Success',
          'green'
        );
        setCheckoutState({
          ...checkoutState,
          loading: false
        });
      },
      onError
    );
  };

  return (
    <HeaderView
      maxViewWidth={1300}
      viewProps={{ style: { gap: isLaptopOrSmaller ? 10 : 20 } }}
    >
      <AppTitle
        style={{
          backgroundColor: isLaptopOrSmaller ? '#FFF' : 'unset',
          padding: isLaptopOrSmaller ? 10 : 0
        }}
      >
        Your Cart
      </AppTitle>

      {userCart && userCart.cart.lineItems.length > 0 && (
        <WsUnavailableProductsAlert
          baseStoreUrl={`/association/${regAssociation?.pkRegAssociation}`}
          ecomStoreCartProducts={userCart.ecomStoreCart?.ecomStoreCartProducts}
          latestVersionEcomStoreCartProducts={
            userCart.ecomStoreCart?.latestVersionEcomStoreCartProducts
          }
          latestVersionRegAssociationDivisionForms={
            userCart.latestVersionRegAssociationDivisionForms
          }
          onNavigate={() => {}}
          onRemoveUnavailableProducts={onRemoveUnavailableProducts}
          regCartProducts={userCart.regCartProducts}
          submitting={checkoutState.loading || checkoutState.loadingCheckout}
        />
      )}

      {hasFinalSaleProducts && (
        <WsCareInstructionsAlert
          baseStoreUrl=""
          ecomStoreCartProducts={userCart.ecomStoreCart.ecomStoreCartProducts}
          hideViewAllLink
          onNavigate={() => {}}
        />
      )}

      <AppFlexbox
        style={{
          alignItems: 'start',
          gap: isLaptopOrSmaller ? 10 : 40,
          flexDirection: isDesktopOrSmaller ? 'column' : 'row',
          flexWrap: 'nowrap'
        }}
      >
        <CheckoutItemList
          isCheckoutSubmitting={checkoutState.loading}
          loading={loading}
          onCustomizeProduct={showCartProductCustomization}
          onRedeemCoupon={onRedeemProductCoupon}
          onRemoveCartProducts={onRemoveCartProducts}
          onRemoveUnavailableProducts={onRemoveUnavailableProducts}
          onUpdateCartProducts={onUpdateCartProducts}
          regCart={userCart}
        />

        <CheckoutOrderSummary
          checkoutPaymentType={checkoutState.paymentType}
          fundraiser={checkoutState.fundraiser}
          hasFinalSaleProducts={hasFinalSaleProducts}
          isCheckoutSubmitting={checkoutState.loading}
          loading={loading}
          onCheckout={createCheckoutSession}
          onFundraiserChange={(fundraiser) => {
            setCheckoutState({
              ...checkoutState,
              fundraiser: {
                ...checkoutState.fundraiser,
                ...fundraiser
              }
            });
            setFundraiser({
              ...checkoutState.fundraiser,
              ...fundraiser
            });
          }}
          regAssociation={regAssociation}
          regCart={userCart}
        />
      </AppFlexbox>

      {merchEnabled && (
        <>
          <AppCard>
            <EcomProductCollectionShowcase
              analyticCta={
                ANALYTIC_MERCH_PACKAGE_SHOWCASE_LOCATION_ENUM.CART_FOOTER
              }
              pkEcomStore={regAssociation.ecomStore.pkEcomStore}
              pkRegAssociation={regAssociation.pkRegAssociation}
              showCollectionTitle
            />
          </AppCard>

          <WebstoreProductsModal
            isCheckoutView
            regAssociation={regAssociation}
          />
        </>
      )}

      <OfflinePaymentModal
        isOpen={checkoutState.showOfflinePaymentModal}
        onClose={() =>
          setCheckoutState({
            ...checkoutState,
            showOfflinePaymentModal: false
          })
        }
        regCart={userCart}
      />

      <ChangeShippingModal />

      <CustomizeCartProductModal
        baseStoreUrl={`/association/${regAssociation?.pkRegAssociation}`}
        colorSchemes={colorSchemes}
        ecomStoreTheme={{ ecomStore: regAssociation?.ecomStore }}
        themeConfig={themeConfig}
      />
      <PreviewWebstoreProductModal />
    </HeaderView>
  );
};

CheckoutCart.propTypes = { hasFetched: PropTypes.bool };

export default CheckoutCart;
